/**
 * Simulated KOL agents built from the supplied roster. Archetypes, sizes, balances and timing are FICTIONAL game
 * settings assigned deterministically from the row id; they are not inferred from the supplied PnL/rank/volume.
 */
import data from './data/kols.json';
import { hashStr, Rng } from './rng';

export type Archetype = 'early' | 'whale' | 'follower' | 'scalper' | 'holder' | 'two-sided';
export interface KolSource { wallet: string; x: string | null; pnl7dUsd: number | null; vol7dUsd: number | null; activeDays: number | null; totalPnlUsd: number | null }
export interface Kol { id: string; name: string; status: string; rank: number | null; src: KolSource; sim: string; archetype: Archetype; balanceSol: number; size: [number, number]; showcase: boolean }

/** Simulation config (chosen values). Sizes in SOL per order; showcase = curated fictional Cupsey SIM profile. */
export const KOL_CFG = {
  archetypes: {
    early: { size: [0.5, 4], delay: [300, 2500], bg: 0.5 },
    whale: { size: [8, 30], delay: [1500, 8000], bg: 0.15 },
    follower: { size: [0.5, 5], delay: [3000, 12000], bg: 0.4 },
    scalper: { size: [1, 6], delay: [500, 4000], bg: 0.6 },
    holder: { size: [1, 8], delay: [2000, 10000], bg: 0.25 },
    'two-sided': { size: [1, 10], delay: [1000, 6000], bg: 0.5 },
  } as Record<Archetype, { size: [number, number]; delay: [number, number]; bg: number }>,
  showcaseSize: [25, 60] as [number, number], // fictional game setting for the Cupsey SIM highlight buy
  balanceSol: [150, 1500] as [number, number],
  catalystFollowers: [3, 8] as [number, number],
  backgroundTradeProb: 0.012, // per active background token per tick (small trades only)
  backgroundMaxSol: 2,
};
const ARCH: Archetype[] = ['early', 'whale', 'follower', 'scalper', 'holder', 'two-sided'];

export const KOLS_ALL: Kol[] = (data as { rows: Omit<Kol, 'sim' | 'archetype' | 'balanceSol' | 'size' | 'showcase'>[] }).rows.map((r) => {
  const h = hashStr(r.id); const showcase = r.name === 'Cupsey';
  const archetype: Archetype = showcase ? 'whale' : ARCH[h % ARCH.length]!;
  const rr = new Rng(h);
  return { ...r, sim: `${r.name} SIM`, archetype, showcase, size: showcase ? KOL_CFG.showcaseSize : KOL_CFG.archetypes[archetype].size, balanceSol: Math.round(rr.range(...KOL_CFG.balanceSol)) };
});
export const KOL_BY_ID = new Map(KOLS_ALL.map((k) => [k.id, k]));
export const KOL_SOURCE = (data as { source: string; note: string });
export const kolWallet = (id: string) => 'kol:' + id;
export const kolOfWallet = (w: string) => (w.startsWith('kol:') ? KOL_BY_ID.get(w.slice(4)) : undefined);
export const CUPSEY_IDS = KOLS_ALL.filter((k) => k.showcase).map((k) => k.id);
/** Default tracked subset (keeps the panel useful; users can track any row in the directory). */
export const DEFAULT_TRACKED = [...CUPSEY_IDS, ...KOLS_ALL.filter((k) => !k.showcase && k.status === 'Listed').slice(0, 10).map((k) => k.id)];
